import type { ReactNode } from "react";

/**
 * Posição das setas next/prev. Dois estilos:
 * - `top-left` / `top-right`: header próprio acima do track (`[título ── nav]`).
 *   O único modo com título. O lado do grupo de setas segue o sufixo.
 * - `top` / `center` / `bottom`: setas flutuando sobre o track (sem header, sem
 *   título), ancoradas na altura indicada. `center` é o comportamento clássico.
 */
export type ScrollNavAlign = "top-left" | "top-right" | "top" | "center" | "bottom";

export type ScrollNavButtonSize = "xs" | "sm" | "md";

/**
 * Estilo visual das setas flutuantes:
 * - `button` (default): botão circular/quadrado (`ScrollNavButton`).
 * - `edge-fade`: seta sobre um gradiente que desvanece a partir da cor do fundo.
 *   A cor NÃO é fixa — vem da CSS var `--nav-fade-color` (fallback transparente),
 *   definida pelo consumidor (ex.: `--nav-fade-color: var(--color-secondary)`).
 */
export type ScrollNavVariant = "button" | "edge-fade";

/** `top-left`/`top-right` renderizam header; os demais flutuam sobre o track. */
export function isHeaderNav(align: ScrollNavAlign): align is "top-left" | "top-right" {
    return align === "top-left" || align === "top-right";
}

export interface ScrollableContainerProps {
    children: ReactNode;
    /**
     * Posição das setas. Ver {@link ScrollNavAlign}. Default `center` (setas
     * flutuando no meio das bordas — comportamento clássico). Ignorado se
     * `showNav` for falso.
     */
    navAlign?: ScrollNavAlign;
    /** Título do trilho. Só é renderizado quando `navAlign` é `top-left`/`top-right`. */
    title?: ReactNode;
    /**
     * Rota do "Ver tudo" exibido ao lado do título (no bloco de título do
     * header). Requer `title` e `navAlign` `top-left`/`top-right`.
     */
    titleHref?: string;
    /**
     * Liga as setas next/prev auto-ocultáveis (aparecem só quando há para onde
     * rolar naquele lado).
     */
    showNav?: boolean;
    /**
     * Tamanho dos botões de nav no modo flutuante (`navAlign` `top`/`center`/
     * `bottom`). Default `md`. No modo header (`top-left`/`top-right`) as setas
     * são sempre `sm`.
     */
    navSize?: ScrollNavButtonSize;
    /**
     * Cola as setas flutuantes na borda do track (`right-0`/`left-0`), sem o
     * recuo padrão do size. Só afeta o modo flutuante (`top`/`center`/`bottom`).
     * Default `false`.
     */
    navEdgeless?: boolean;
    /**
     * Formato dos botões de nav. `full` (default, circular) ou `md` (quadrado
     * com cantos arredondados). Só afeta o modo flutuante.
     */
    navRounded?: "full" | "md";
    /**
     * Estilo das setas flutuantes. `button` (default, `ScrollNavButton`) ou
     * `edge-fade` (seta sobre um gradiente que desvanece a partir da cor do
     * fundo, lida da CSS var `--nav-fade-color`). Ver {@link ScrollNavVariant}.
     * Só afeta o modo flutuante (`top`/`center`/`bottom`).
     */
    navVariant?: ScrollNavVariant;
    /**
     * Recuo lateral (full-bleed). `"page"` usa `--page-gutter` (token da
     * página); `"none"` zera; qualquer outra string é usada como CSS (ex.:
     * `"1rem"`). O recuo vira o offset do 1º item e o snap; o conteúdo sangra
     * até a borda ao rolar. Default `"none"`.
     */
    edge?: "page" | "none" | (string & {});
    /**
     * Alinhamento do conteúdo quando ele cabe no track (não há overflow).
     * `center` (default) centraliza; `start` cola à esquerda. Ao estourar, o
     * conteúdo rola normalmente em ambos os casos.
     */
    align?: "center" | "start";
    /**
     * Distribuição dos itens no eixo horizontal. `start` (default) agrupa à
     * esquerda; `between` aplica `justify-between` (distribui a sobra entre os
     * itens quando cabem no track). Só tem efeito visível quando o conteúdo NÃO
     * estoura o track. Requer `align="start"` para o wrapper ocupar 100%.
     */
    justify?: "start" | "between";
    /**
     * Espaço entre itens. Número → px; string → CSS literal. `null`/omitido →
     * gap responsivo default por classe (`gap-2 md:gap-4 lg:gap-8`).
     */
    gap?: number | string | null;
    /** Passo das setas: px ou `page` (largura visível do trilho + gap). Default `300`. */
    scrollStep?: number | "page";
    className?: string;
    onScroll?: () => void;
}

export interface ScrollNavButtonProps {
    side: "left" | "right";
    onClick: () => void;
    disabled?: boolean;
    size?: ScrollNavButtonSize;
    /**
     * Ancoragem quando a seta flutua sobre o track: `center` (default,
     * inset-y+my-auto), `top` ou `bottom`. Ignorado no modo header (as setas
     * ficam em fluxo normal, lado a lado).
     */
    floatY?: "center" | "top" | "bottom";
    /** Quando true, a seta é posicionada em `absolute` sobre o track (modo flutuante). */
    floating?: boolean;
    /** Cola a seta na borda do track (`left-0`/`right-0`), sem o recuo do size. */
    edgeless?: boolean;
    /** Formato do botão: `full` (default, circular) ou `md` (quadrado arredondado). */
    rounded?: "full" | "md";
    className?: string;
}
